import { useState } from "react";
import { Alert, Box, Button, Chip, List, ListItem, Snackbar, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { navigateFromClick } from "shared/services/navigationClick";
import { myWorkApi, myWorkKeys, WorkItem, WorkKind } from "../services/my-work-api";

export function MyWorkItemList({ items, kind, onNavigate }: { items: WorkItem[]; kind: WorkKind; onNavigate?: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const client = useQueryClient();
    const [notice, setNotice] = useState("");
    const claim = useMutation({ mutationFn: (item: WorkItem) => myWorkApi.claim(item.source, item.id),
        onSuccess: () => { setNotice(String(t("my_work.claim_success"))); client.invalidateQueries({ queryKey: myWorkKeys.all }); },
        onError: (error: any) => {
            setNotice(String(t(error?.response?.status === 409 ? "my_work.errors.already_claimed" : "my_work.errors.not_eligible")));
            client.invalidateQueries({ queryKey: myWorkKeys.all });
        } });
    if (!items.length) return <Typography sx={{ p: 2 }}>{t(`my_work.empty.${kind === "task" ? "tasks" : "approvals"}`)}</Typography>;
    return <>
        <List disablePadding>{items.map(item => <ListItem key={`${item.source}:${item.id}`} divider sx={{ display: "block", py: 1.5 }}>
            <Box component="a" href={item.url} onClick={event => {
                event.preventDefault(); navigateFromClick(event, navigate, item.url); onNavigate?.();
            }} sx={{ color: "text.primary", textDecoration: "none", display: "block", fontWeight: 600,
                "&:hover": { textDecoration: "underline" }, "&:focus-visible": { outline: "2px solid currentColor" } }}>
                {item.title || item.number_ref || item.id}
            </Box>
            {item.number_ref && <Typography variant="caption" sx={{ display: "block" }}>{item.number_ref}</Typography>}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                <Chip size="small" label={t(`my_work.source.${item.source === "fluss_task" ? "fluss" : "record"}`)} />
                <Chip size="small" variant="outlined" label={item.assignment === "group" ?
                    t("my_work.assignment.group", { group: item.group_label || "" }) : t("my_work.assignment.me")} />
                {item.due_at && <Typography variant="caption" color={item.overdue ? "error.main" : "text.secondary"}>
                    {item.overdue ? t("my_work.overdue") : t("my_work.due", { date: new Date(item.due_at).toLocaleDateString() })}
                </Typography>}
            </Box>
            {item.claimable && <Button size="small" sx={{ mt: 1 }} disabled={claim.isPending && claim.variables?.id === item.id}
                onClick={() => claim.mutate(item)}>{t("my_work.claim")}</Button>}
        </ListItem>)}</List>
        <Snackbar open={Boolean(notice)} autoHideDuration={4000} onClose={() => setNotice("")}>
            <Alert onClose={() => setNotice("")} severity="info">{notice}</Alert>
        </Snackbar>
    </>;
}
